// A scene's fighters as figures, on the bodies module (phase B joined; the ragdoll lab's view, src/bodies/view.ts, made
// general for the dojo, the arena and the valley): one figure per fighter from its card, posed between sim steps at the
// frame's alpha, moved by the figure's motion, then the bodies module's pose track sampled at the frame's time on the
// pose layer (an action pose from `combat:move`, a reaction from `combat:hit`); a card change (a boss's next phase)
// rebuilds the figure; a defeated fighter's standing figure hands over to its ragdoll's view (posed from the Rapier
// bodies, carved by its part list), which bursts into motes as it dissolves; a figure whose fighter is gone goes.
// A view: it reads the sim and never writes it.
import * as THREE from "three";
import { applyPoses, buildFigure, createMotion, createRagdollView, resetTurns, type Attachment, type Figure, type FigureCard, type Locomotion, type Motion, type RagdollView } from "@rpg/figure";
import type { LowPolyLook } from "@rpg/look";
import type { CollisionWorld } from "@rpg/collision";
import type { Runtime } from "@rpg/runtime";
import type { SpikeContent } from "../content";
import type { Bodies } from "./module";
import { createMotes, type Motes } from "./motes";

type V3 = readonly [number, number, number];

/** One fighter as the scene sees it this step. */
export interface FigureEntry {
  id: number;
  card: string;
  /** Feet. */
  pos: V3;
  yaw: number;
  vel: V3;
  grounded?: boolean;
  /** The attachment on the hand socket (a weapon), or null. Undefined leaves it. */
  attachment?: string | null;
  /** Its arms out (a weapon drawn) besides what the poses say. */
  acting?: boolean;
  /** Where its head looks (null: ahead). */
  lookAt?: { x: number; y: number; z: number } | null;
}

/** The scene's figures. */
export interface FigureSet {
  /** The standing figure of a fighter, if any. */
  figure(id: number): Figure | null;
  /** A fighter's weapon now (its socket's base and its attachment's far end, world space), for trails. */
  weapon(id: number): { base: [number, number, number]; tip: [number, number, number] } | null;
  report(): { standing: number; ragdolls: number; motes: number };
}

interface Standing {
  fig: Figure;
  motion: Motion;
  card: string;
  prev: { x: number; y: number; z: number; yaw: number };
  curr: { x: number; y: number; z: number; yaw: number };
  held: string | null;
}
interface Fallen {
  fig: Figure;
  view: RagdollView;
  card: string;
  burst: boolean;
}

const lerpYaw = (a: number, b: number, t: number): number => a + Math.atan2(Math.sin(b - a), Math.cos(b - a)) * t;

/** Mount the figures. `entries` is read at each sim step's end (where they stand) and each frame (who stands, their
 *  cards, weapons and motion). */
export function mountFigureSet(o: {
  runtime: Runtime;
  scene: THREE.Object3D;
  look: LowPolyLook;
  content: SpikeContent;
  bodies: () => Bodies;
  collision: () => CollisionWorld;
  entries: () => readonly FigureEntry[];
  /** The ground under (x, z) from `fromY` down (the feet's probe), or null. */
  probe: (x: number, z: number, fromY: number) => number | null;
  /** A figure was made (flash shells), or is going. */
  onAdd?(id: number, root: THREE.Object3D): void;
  onRemove?(id: number): void;
  /** Late-phase order (default -15, beside the hero's). */
  order?: number;
  name?: string;
}): FigureSet {
  const reg = o.content.registry;
  const cards = new Map<string, FigureCard>(reg.rows("cards").map((c) => [c.id, c as unknown as FigureCard]));
  const attachments = new Map<string, Attachment>(reg.rows("attachments").map((a) => [a.id, a as unknown as Attachment]));
  const blobOf = (card: string): number | undefined => (reg.get("cards", card) as { blob?: number } | undefined)?.blob;
  const make = (card: string, id: number): Figure => {
    const c = cards.get(card);
    if (!c) throw new Error(`figures: no card "${card}"`);
    const fig = buildFigure(c, { material: o.look.surface, colour: o.look.palette });
    fig.root.name = `figure:${card}:${id}`;
    o.scene.add(fig.root);
    o.look.track(fig.root, { blob: blobOf(card) });
    return fig;
  };
  const standing = new Map<number, Standing>();
  const fallen = new Map<string, Fallen>();
  const motes: Motes = createMotes(o.scene, o.look.surface);
  const glow = (card: string): THREE.Color => o.look.palette(card === "wanderer" ? "hero.gold" : (reg.get("cards", card)?.tier ?? "") === "enemy" ? "enemy.glow" : "hero.cream");
  const tips = new Map<string, THREE.Vector3>();

  const drop = (id: number): void => {
    const s = standing.get(id);
    if (!s) return;
    o.onRemove?.(id);
    o.look.untrack(s.fig.root);
    s.fig.root.removeFromParent();
    s.fig.dispose();
    standing.delete(id);
  };
  const ensure = (e: FigureEntry): Standing => {
    let s = standing.get(e.id);
    if (s && s.card !== e.card) {
      drop(e.id);
      s = undefined;
    }
    if (!s) {
      const fig = make(e.card, e.id);
      const motion = createMotion(fig, cards.get(e.card)!.motion!, o.probe);
      const at = { x: e.pos[0], y: e.pos[1], z: e.pos[2], yaw: e.yaw };
      fig.root.position.set(at.x, at.y, at.z);
      fig.root.rotation.y = at.yaw;
      motion.reset();
      s = { fig, motion, card: e.card, prev: at, curr: { ...at }, held: null };
      standing.set(e.id, s);
      o.onAdd?.(e.id, fig.root);
    }
    return s;
  };

  // the fighters standing now get their figures at once, before the scene's warm-up render (their programs and the
  // shadow pass's compile then, not in play)
  for (const e of o.entries()) ensure(e);

  // each sim step: where each fighter stands (the frame poses between the last two)
  o.runtime.on(
    "sim",
    () => {
      for (const e of o.entries()) {
        const s = standing.get(e.id);
        if (!s) continue;
        s.prev = s.curr;
        s.curr = { x: e.pos[0], y: e.pos[1], z: e.pos[2], yaw: e.yaw };
        if (Math.hypot(s.curr.x - s.prev.x, s.curr.z - s.prev.z) > 2) s.prev = { ...s.curr };
      }
    },
    { order: 1e8, name: `${o.name ?? "figures"}: states` },
  );

  o.runtime.on(
    "late",
    (c) => {
      if (c.mode === "play" && o.runtime.paused) return;
      const B = o.bodies();
      const bs = B.state();
      const tFrame = bs.t + (c.alpha - 1) * o.runtime.clock.dt;
      const world = o.collision();
      // ragdolls: the standing figure hands over to its ragdoll's view (a fresh figure after a restore)
      const live = new Set<string>();
      for (const r of bs.ragdolls) {
        live.add(r.state.id);
        let f = fallen.get(r.state.id);
        if (!f) {
          const st = standing.get(r.entity);
          let fig: Figure;
          if (st && st.card === r.card) {
            fig = st.fig;
            o.onRemove?.(r.entity);
            standing.delete(r.entity);
          } else fig = make(r.card, r.entity);
          o.look.untrack(fig.root);
          o.look.track(fig.root);
          f = { fig, view: createRagdollView(fig, B.resolved(r.card)!), card: r.card, burst: false };
          fallen.set(r.state.id, f);
        }
        f.view.update(world, r.state, c.dt);
        if (r.state.phase === "dissolving" && !f.burst) {
          f.burst = true;
          motes.burst(f.view.centres(), 10 + 6 * f.view.centres().length, glow(f.card), Number(r.state.id.slice(2)) * 7919);
        }
      }
      for (const [id, f] of fallen) {
        if (live.has(id)) continue;
        if (!f.burst) motes.burst(f.view.centres(), 10 + 6 * f.view.centres().length, glow(f.card), Number(id.slice(2)) * 7919);
        o.look.untrack(f.fig.root);
        f.fig.root.removeFromParent();
        f.fig.dispose();
        fallen.delete(id);
      }
      // standing fighters: the motion, then the pose layer at the frame's time
      const ragdolled = new Set(bs.ragdolls.map((r) => r.entity));
      const seen = new Set<number>();
      const n = Math.min(90, Math.max(1, Math.ceil(c.dt * 60 - 1e-6)));
      for (const e of o.entries()) {
        if (ragdolled.has(e.id)) continue;
        seen.add(e.id);
        const s = ensure(e);
        if (e.attachment !== undefined && e.attachment !== s.held && s.fig.card.socket) {
          s.held = e.attachment;
          s.fig.attach(e.attachment ? (attachments.get(e.attachment) ?? null) : null);
        }
        const a = c.alpha;
        s.fig.root.position.set(s.prev.x + (s.curr.x - s.prev.x) * a, s.prev.y + (s.curr.y - s.prev.y) * a, s.prev.z + (s.curr.z - s.prev.z) * a);
        s.fig.root.rotation.y = lerpYaw(s.prev.yaw, s.curr.yaw, a);
        const sample = B.sample(e.id, e.card, tFrame);
        const loco: Locomotion = {
          speed: Math.hypot(e.vel[0], e.vel[2]),
          velocity: { x: e.vel[0], y: e.vel[1], z: e.vel[2] },
          grounded: e.grounded ?? true,
          jump: false,
          glide: false,
          acting: sample.arms || (e.acting ?? false),
          lookAt: e.lookAt ?? null,
        };
        for (let i = 0; i < n; i++) {
          resetTurns(s.fig);
          s.motion.update(c.dt / n, loco);
        }
        applyPoses(s.fig, sample);
      }
      for (const id of [...standing.keys()]) if (!seen.has(id)) drop(id);
      motes.update(Math.min(c.dt, 0.25));
    },
    { order: o.order ?? -15, name: `${o.name ?? "figures"}: pose` },
  );

  return {
    figure: (id) => standing.get(id)?.fig ?? null,
    weapon(id) {
      const s = standing.get(id);
      return s && s.held ? sampleWeapon(s.fig, s.held, tips) : null;
    },
    report: () => ({ standing: standing.size, ragdolls: fallen.size, motes: motes.alive() }),
  };
}

const _v = new THREE.Vector3();

/** A figure's weapon now (world space): its hand socket's position and the attachment's far end, the furthest vertex of
 *  the socket's mesh from the grip (measured once per weapon id into `tips`). Null without a socket. */
export function sampleWeapon(fig: Figure, held: string, tips: Map<string, THREE.Vector3>): { base: [number, number, number]; tip: [number, number, number] } | null {
  const socket = fig.card.socket ? fig.joints.get(fig.card.socket) : undefined;
  if (!socket) return null;
  let tip = tips.get(held);
  if (!tip) {
    const p = fig.meshes.get(fig.card.socket!)?.geometry.getAttribute("position");
    tip = new THREE.Vector3(0, -0.8, 0);
    let best = 0;
    if (p)
      for (let i = 0; i < p.count; i++) {
        _v.fromBufferAttribute(p, i);
        const d = _v.lengthSq();
        if (d > best) {
          best = d;
          tip.copy(_v);
        }
      }
    tips.set(held, tip);
  }
  socket.updateWorldMatrix(true, false);
  const b = socket.getWorldPosition(new THREE.Vector3());
  const t = socket.localToWorld(tip.clone());
  return { base: [b.x, b.y, b.z], tip: [t.x, t.y, t.z] };
}
